import Phaser from 'phaser';
import type { ZoneDef } from '../layout/zones.js';
import { ChipStackVisual } from './ChipStackVisual.js';
import type { ShimmerClock } from './ShimmerClock.js';
import { attachPressGesture, type PressGestureHandle } from './pressGesture.js';

export interface AtsBetZoneEvents {
  onClick: (zone: AtsBetZone) => void;
  onLongPress?: (zone: AtsBetZone, pointer: Phaser.Input.Pointer) => void;
  /** Fires on pointerover (desktop only — gated by the hover/pointer media query). */
  onHover?: (zone: AtsBetZone) => void;
  /** Fires on pointerout. Always fires when the corresponding onHover did. */
  onHoverEnd?: (zone: AtsBetZone) => void;
}

export interface AtsBetZoneOptions {
  scene: Phaser.Scene;
  /** Rectangle the zone fills, in canvas-buffer px. */
  rect: { x: number; y: number; w: number; h: number };
  /** Display-only zone def (id, type, label). polygon is unused here. */
  def: ZoneDef;
  /**
   * Required numbers for this zone. SMALL = [2,3,4,5,6] (1 row), TALL =
   * [8,9,10,11,12] (1 row), ALL = [2,3,4,5,6,8,9,10,11,12] (2 rows of 5).
   */
  numbers: number[];
  /** When numbers.length > 5, render as 2 rows of 5. */
  twoRows: boolean;
  /**
   * Font size for the side-column numbers. Passed in by TableScene so
   * SMALL / ALL / TALL all render at the same glyph size despite their
   * cell heights differing (ALL gets a larger height share to fit 5
   * numbers per column).
   */
  numberFontSize: number;
  /** Optional shimmer clock — when provided, the chip-stack badge participates
   *  in the scene-wide gold-wash sweep. Display zones with no badge can omit it. */
  shimmerClock?: ShimmerClock | undefined;
  events: AtsBetZoneEvents;
}

// Felt-matched palette — reads as part of the painted layout, not a UI panel.
// FELT_GREEN matches `#0d3b2a` from the SVG `<rect>` background; outlines
// use the same cream the felt uses for its border art.
const FELT_GREEN  = 0x0d3b2a;
const FELT_OUTLINE = 0xf7f3e7;
const DIM_TEXT    = '#f7f3e7';   // cream — matches felt label text
const LIT_TEXT    = '#ffd24a';   // bright yellow — pops against dark felt
const PULSE_GOLD  = '#fff58a';   // brighter flash on newly-lit transition
const RED_FLASH   = '#ff5d5d';
const SVG_GOLD    = '#ffff00';   // matches the SVG felt's fill:#ff0 yellow

/**
 * Phaser container for one ATS bet zone (Small / Tall / All). Renders a
 * gold-bordered rect with a header label, lit/dim bubbles for each required
 * number, and a chip stack at the bottom. Tap places a bet of the rack's
 * selected denom; long-press removes a chip.
 *
 * Lit-state is data-driven via setLitNumbers; the bridge calls this on
 * RollResolved to reflect the bet's metadata.seen set.
 */
export class AtsBetZone extends Phaser.GameObjects.Container {
  private background: Phaser.GameObjects.Graphics;
  private highlight: Phaser.GameObjects.Graphics;
  private chipStack: ChipStackVisual;
  private bubbles: Map<number, BubbleVisual> = new Map();
  private currentBetCents = 0;
  private pressGesture?: PressGestureHandle;

  readonly def: ZoneDef;
  /** Cell rect this zone fills — exposed so the tutorial's highlight
   *  overlay can wrap the whole tile (numbers + box). */
  readonly rect: { x: number; y: number; w: number; h: number };

  /**
   * The square click target — bet zone proper. Bubbles render outside this
   * rect so chip stacks can sit on the square without covering the lit
   * progress display. Cached so the hit-area + chip anchor + active-glow
   * stroke all use the same coordinates.
   */
  private targetRect: { x: number; y: number; w: number; h: number };

  /** Border stroke width — derived from the felt SVG so the line reads as
   * part of the painted layout. See constructor for the math. */
  private borderWidth = 1;

  constructor(opts: AtsBetZoneOptions) {
    super(opts.scene, 0, 0);
    this.def = opts.def;
    this.rect = opts.rect;
    const { scene, rect, numbers, events } = opts;
    const dpr = (scene.game.registry.get('dpr') as number | undefined) ?? 1;
    // Match BetZone's orientation-aware chip-scale factor so an ATS chip
    // stack is sized like every other on-table chip stack. Portrait uses
    // a phone-width reference clamped at the 0.85 floor; landscape uses
    // the smaller of the canvas-aspect ratios so the chip scales with the
    // panel sizing rather than canvas width alone.
    const W = scene.scale.width;
    const H = scene.scale.height;
    const sm = H > W
      ? Math.max(0.85, W / 476.25)
      : Math.min(W / 1280, H / 800);
    const px = (n: number) => Math.round(n * dpr);

    // Border width matches the felt SVG. The SVG strokes are 2 SVG units
    // in a 226-unit hardway viewBox, rasterized at hardwaySize buffer px
    // — so the visible stroke is `2 * hardwaySize / 226` buffer px (~4 on
    // a typical portrait phone). Computing it here from the same layout
    // formula the table uses keeps our border in lockstep with the felt's.
    const sceneW = scene.scale.width;
    const sceneH = scene.scale.height;
    const hardwaySize = Math.min(Math.round(sceneH * 0.30), Math.round(sceneW * 0.55));
    this.borderWidth = Math.max(1, Math.round((2 / 226) * hardwaySize));

    // Uniform font size across all three zones — passed in from the
    // strip-level layout so it's the same for SMALL / ALL / TALL even
    // though their cell heights differ.
    const numberFontSize = opts.numberFontSize;

    // Single layout for all three zones: a narrow centered square click
    // target with two vertical columns of numbers in the gutters. SMALL/TALL
    // get 2 + 3, ALL gets 5 + 5. Square dimensions are identical across all
    // three zones so they read as a matched set.
    const half = Math.ceil(numbers.length / 2);
    const leftNumbers  = numbers.slice(0, numbers.length - half);
    const rightNumbers = numbers.slice(numbers.length - half);

    // Layout constants mirror tableLayout's atsContentW math: cardW is
    // sized to fit exactly `targetW + 2*sideGap + 2*numHalfW`, so the
    // visible content fills the cell with no dead space on either side.
    // Drift these formulas and the layout's three-margin balance breaks.
    // +5 extra buffer-px so the number columns sit clear of the box
    // edge instead of hugging it. Mirror in tableLayout's atsContentW
    // calc — both must add the same constant or the cell + content
    // widths drift.
    const sideGap = Math.round(numberFontSize * 0.6) + 5;
    const targetW = Math.round(numberFontSize * 2.5);
    const targetH = rect.h - px(8);
    const targetX = rect.x + Math.round((rect.w - targetW) / 2);
    const targetY = rect.y + px(4);
    const leftCx  = targetX - sideGap;
    const rightCx = targetX + targetW + sideGap;
    this.targetRect = { x: targetX, y: targetY, w: targetW, h: targetH };

    this.background = scene.add.graphics();
    this.drawBackground();
    this.add(this.background);

    // Hover highlight — same 0.12 white wash BetZone uses on the main board,
    // shown on pointerover and hidden on pointerout. Sits above the felt
    // background but below the label/numbers/chips so they stay readable.
    this.highlight = scene.add.graphics();
    this.highlight.fillStyle(0xffffff, 0.12);
    this.highlight.fillRect(targetX, targetY, targetW, targetH);
    this.highlight.setVisible(false);
    this.add(this.highlight);

    this.makeLabel(scene, opts.def.label, targetX, targetY, targetW, targetH, px);

    // Side columns: stride scales to count so a 5-tall ALL column packs
    // tighter than SMALL/TALL's 2- or 3-tall column. Topmost / bottommost
    // entries naturally read as the row anchors at the cell edges.
    const sideY = rect.y + px(2);
    const sideH = rect.h - px(4);
    const placeColumn = (nums: number[], areaCx: number) => {
      if (nums.length === 0) return;
      const stride = sideH / nums.length;
      for (let i = 0; i < nums.length; i++) {
        const n = nums[i]!;
        const cy = sideY + stride * i + stride / 2;
        this.addNumber(scene, n, areaCx, cy, numberFontSize);
      }
    };
    placeColumn(leftNumbers, leftCx);
    placeColumn(rightNumbers, rightCx);

    // Chip stack centered on the SQUARE — anchor is the bottom-chip center,
    // and chips stack UPWARD. Anchor sits ~1/3 of the height up from the
    // bottom edge so a single chip rests in the lower third of the square
    // (rather than hugging the bottom border) and a tall stack still has
    // room to grow upward without overlapping the label.
    const chipRadius = Math.round(14 * sm);
    const stackAnchor = {
      x: targetX + targetW / 2,
      y: targetY + Math.round(targetH * 2 / 3),
    };
    this.chipStack = new ChipStackVisual({
      scene,
      anchor: stackAnchor,
      dpr,
      chipRadius,
      depth: 5,
      shimmerClock: opts.shimmerClock,
    });

    // Hit area covers ONLY the square — bubbles are display-only so they
    // can extend past the click region without intercepting taps.
    this.setInteractive({
      hitArea: new Phaser.Geom.Rectangle(targetX, targetY, targetW, targetH),
      hitAreaCallback: Phaser.Geom.Rectangle.Contains,
      useHandCursor: true,
    });

    const hasHover = typeof window !== 'undefined'
      && typeof window.matchMedia === 'function'
      && window.matchMedia('(hover: hover)').matches;
    if (hasHover) {
      this.on('pointerover', () => {
        this.highlight.setVisible(true);
        events.onHover?.(this);
      });
      this.on('pointerout', () => {
        this.highlight.setVisible(false);
        events.onHoverEnd?.(this);
      });
    }

    this.pressGesture = attachPressGesture({
      scene,
      target: this,
      ringCenter: stackAnchor,
      ringRadius: Math.round(40 * dpr),
      ringStroke: Math.max(4, Math.round(6 * dpr)),
      onTap: () => events.onClick(this),
      onLongPress: () => events.onLongPress?.(this, scene.input.activePointer),
      canLongPress: () => this.currentBetCents > 0,
    });

    scene.add.existing(this);
  }

  override preDestroy() {
    this.pressGesture?.destroy();
    delete this.pressGesture;
    this.chipStack.destroy();
    super.preDestroy();
  }

  /** Returns the click target's bounding rect for tooltip anchoring. */
  hoverAnchor(): { x: number; y: number; w: number; h: number } {
    return { ...this.targetRect };
  }

  private drawBackground() {
    this.background.clear();
    this.background.fillStyle(FELT_GREEN, 1);
    this.background.fillRect(
      this.targetRect.x, this.targetRect.y,
      this.targetRect.w, this.targetRect.h,
    );
    // Single white line whose width matches the felt SVG strokes — looks
    // like part of the painted layout instead of an overlaid UI panel.
    this.background.lineStyle(this.borderWidth, FELT_OUTLINE, 1);
    this.background.strokeRect(
      this.targetRect.x, this.targetRect.y,
      this.targetRect.w, this.targetRect.h,
    );
  }

  /** Vertical letter-stacked label centered in the square. */
  private makeLabel(
    scene: Phaser.Scene,
    label: string,
    targetX: number, targetY: number,
    targetW: number, targetH: number,
    px: (n: number) => number,
  ): void {
    const labelText = label.split('').join('\n');
    const labelFontSize = Math.max(
      9,
      Math.min(px(14), Math.round(targetH / labelText.split('\n').length / 1.3)),
    );
    const text = scene.add.text(
      targetX + targetW / 2,
      targetY + targetH / 2,
      labelText,
      {
        fontFamily: 'Times New Roman, Georgia, serif',
        fontSize: `${labelFontSize}px`,
        color: SVG_GOLD,
        fontStyle: 'bold',
        align: 'center',
      },
    ).setOrigin(0.5, 0.5).setLineSpacing(-2);
    this.add(text);
  }

  /** Add one numbered "mark" at (cx, cy) and register it in the bubbles map. */
  private addNumber(scene: Phaser.Scene, n: number, cx: number, cy: number, fontSize: number) {
    // BubbleVisual derives its font from sizeHint * 1.5; reverse the math so
    // the resulting glyph matches the explicit fontSize argument.
    const sizeHint = fontSize / 1.5;
    const bubble = new BubbleVisual(scene, cx, cy, sizeHint, n);
    this.add(bubble.root);
    this.bubbles.set(n, bubble);
  }

  /** Drives the bubble lit/dim state from the bet's metadata.seen set. */
  setLitNumbers(numbers: number[]): void {
    const lit = new Set(numbers);
    for (const [n, bubble] of this.bubbles) bubble.setLit(lit.has(n));
  }

  /**
   * Brief 200ms scale-pulse on a single bubble. Color: 'gold' when the
   * number was just rolled and added to the seen set; 'red' on 7-out for
   * the dramatic loss feedback (called on every bubble in sequence by the
   * bridge).
   */
  pulseNumber(n: number, color: 'gold' | 'red'): void {
    const bubble = this.bubbles.get(n);
    if (!bubble) return;
    bubble.pulse(color);
  }

  /**
   * Bet-active state has no visual treatment — the visible chip stack
   * alone communicates "money on this zone". Kept as a separate method
   * (not Phaser's inherited `setActive`) because that one controls input
   * enable/disable, which would break click handling if toggled false.
   */
  setBetActive(_value: boolean): void {
    // intentionally a no-op
  }

  setChipStackCents(cents: number): void {
    this.currentBetCents = cents;
    this.chipStack.setCents(cents);
  }

  /** Anchor for winnings-chip fly-off. */
  getWinningsSpawnAnchor(chipRadius: number): { x: number; y: number } {
    const a = this.chipStack.getAnchor();
    return { x: a.x - chipRadius * 2, y: a.y };
  }

  /** Centre of the bottom chip — fly-in / fly-out target. */
  getChipStackAnchor(): { x: number; y: number } {
    return this.chipStack.getAnchor();
  }

  /** Per-chip radius this zone's stack was built with — match it when
   *  spawning transient fly chips so they read at the same visual size. */
  getChipRadius(): number {
    return this.chipStack.getChipRadius();
  }
}

/**
 * One numbered "mark" inside an ATS zone — just a number painted on the
 * felt. Dim = cream text (number not yet rolled), lit = bright yellow
 * (number rolled this bet's lifetime). On a newly-lit transition the
 * number flashes brighter and scales up briefly; on 7-out it flashes red.
 *
 * The class name is "BubbleVisual" for historical reasons; the original
 * design called for circle-and-number bubbles. Per playtest feedback the
 * circles were dropped — readability and felt-style minimalism win.
 */
class BubbleVisual {
  readonly root: Phaser.GameObjects.Container;
  private text: Phaser.GameObjects.Text;
  private litColor = LIT_TEXT;

  constructor(scene: Phaser.Scene, cx: number, cy: number, sizeHint: number, n: number) {
    this.root = scene.add.container(cx, cy);
    // Font size scales with the available space; 1.5x the radius hint
    // makes the number the dominant element (no circle to compete with).
    const fontSize = Math.max(11, Math.round(sizeHint * 1.5));
    this.text = scene.add.text(0, 0, String(n), {
      fontFamily: 'Times New Roman, Georgia, serif',
      fontSize: `${fontSize}px`,
      color: DIM_TEXT,
      fontStyle: 'bold',
    }).setOrigin(0.5);
    this.root.add(this.text);
  }

  setLit(lit: boolean) {
    this.text.setColor(lit ? this.litColor : DIM_TEXT);
  }

  pulse(color: 'gold' | 'red') {
    const flash = color === 'gold' ? PULSE_GOLD : RED_FLASH;
    const settle = color === 'gold' ? this.litColor : DIM_TEXT;
    this.text.setColor(flash);
    this.root.scene.tweens.add({
      targets: this.root,
      scale: 1.4,
      duration: 120,
      yoyo: true,
      ease: 'Sine.InOut',
      onComplete: () => this.text.setColor(settle),
    });
  }
}
